/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { useDataSource } from '../data/useDataSource';
import { useAuth } from '../hooks/useAuth';
import { calendars as calendarsApi } from '../api/index';

export const CalendarDataContext = createContext(null);

/**
 * Provider for calendar data - wraps data source and provides CRUD operations
 * Supports multi-calendar management for authenticated users
 */
export function CalendarDataProvider({ children }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  
  // Calendar list state (for authenticated users)
  const [calendarList, setCalendarList] = useState({ owned: [], shared: [] });
  const [currentCalendarId, setCurrentCalendarId] = useState(null);
  const [calendarsLoading, setCalendarsLoading] = useState(false);
  
  // Load calendars when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setCalendarList({ owned: [], shared: [] });
      setCurrentCalendarId(null);
      return;
    }
    
    let mounted = true;
    
    async function loadCalendars() {
      setCalendarsLoading(true);
      try {
        const data = await calendarsApi.list();
        if (mounted) {
          setCalendarList(data);
          // Select default calendar or first owned calendar
          const defaultCal = data.owned.find(c => c.isDefault) || data.owned[0];
          if (defaultCal && !currentCalendarId) {
            setCurrentCalendarId(defaultCal.id);
          }
        }
      } catch (err) {
        console.error('Failed to load calendars:', err);
      } finally {
        if (mounted) {
          setCalendarsLoading(false);
        }
      }
    }
    
    loadCalendars();
    
    return () => {
      mounted = false;
    };
    // Don't include currentCalendarId - it's set by this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);
  
  // Get active calendar ID (for data source)
  const activeCalendarId = isAuthenticated ? currentCalendarId : 'default';
  
  // Get data for active calendar
  const dataSource = useDataSource(activeCalendarId);
  
  // Current calendar object
  const currentCalendar = useMemo(() => {
    if (!isAuthenticated) {
      return { id: 'default', name: 'My Calendar', isDefault: true, color: '#4285f4' };
    }
    return (
      calendarList.owned.find(c => c.id === currentCalendarId) ||
      calendarList.shared.find(c => c.id === currentCalendarId) ||
      null
    );
  }, [isAuthenticated, calendarList, currentCalendarId]);
  
  // Switch to a different calendar
  const switchCalendar = useCallback((calendarId) => {
    setCurrentCalendarId(calendarId);
  }, []);
  
  // Create a new calendar
  const createCalendar = useCallback(async (data) => {
    const newCalendar = await calendarsApi.create(data);
    setCalendarList(prev => ({
      ...prev,
      owned: [...prev.owned, newCalendar],
    }));
    return newCalendar;
  }, []);
  
  // Update a calendar
  const updateCalendar = useCallback(async (id, data) => {
    const updated = await calendarsApi.update(id, data);
    setCalendarList(prev => ({
      ...prev,
      owned: prev.owned.map(c => c.id === id ? { ...c, ...updated } : c),
    }));
    return updated;
  }, []);
  
  // Delete a calendar
  const deleteCalendar = useCallback(async (id) => {
    await calendarsApi.delete(id);
    setCalendarList(prev => ({
      ...prev,
      owned: prev.owned.filter(c => c.id !== id),
    }));
    
    // If we deleted the current calendar, switch to default
    if (id === currentCalendarId) {
      const defaultCal = calendarList.owned.find(c => c.isDefault && c.id !== id);
      if (defaultCal) {
        setCurrentCalendarId(defaultCal.id);
      }
    }
  }, [currentCalendarId, calendarList.owned]);
  
  const value = useMemo(() => {
    const combinedLoading = authLoading || calendarsLoading || dataSource.isLoading;
    
    // Extract isLoading from dataSource to avoid overwriting combined state
    // eslint-disable-next-line no-unused-vars
    const { isLoading, ...dataSourceRest } = dataSource;
    
    return {
      // Calendar management
      calendars: calendarList,
      currentCalendar,
      currentCalendarId,
      switchCalendar,
      createCalendar,
      updateCalendar,
      deleteCalendar,
      
      // Data from active calendar (excluding isLoading)
      ...dataSourceRest,
      
      // Combined loading state (must come after spread to not be overwritten)
      isLoading: combinedLoading,
    };
  }, [
    calendarList,
    currentCalendar,
    currentCalendarId,
    switchCalendar,
    createCalendar,
    updateCalendar,
    deleteCalendar,
    authLoading,
    calendarsLoading,
    dataSource,
  ]);
  
  return (
    <CalendarDataContext.Provider value={value}>
      {children}
    </CalendarDataContext.Provider>
  );
}

/**
 * Hook to access calendar data context
 * @returns {Object} Calendar data and CRUD operations
 */
export function useCalendarDataContext() {
  const context = useContext(CalendarDataContext);
  if (!context) {
    throw new Error('useCalendarDataContext must be used within CalendarDataProvider');
  }
  return context;
}
